(() => {
  if (customElements.get('maison-variation-picker')) return;
  const requests = new Map();
  const hash = value => {
    let result = 2166136261;
    for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619) >>> 0;
    return result;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const unique = values => [...new Set(values)];
  const sorted = values => unique(values).sort((a, b) => a.localeCompare(b));
  const finish = item => [item.f, item.p].filter(Boolean).join(' · ');
  const signature = item => [item.c, item.m, item.f, item.p].join('|');
  const load = url => {
    if (!requests.has(url)) requests.set(url, fetch(url, { credentials: 'omit' }).then(response => {
      if (!response.ok) throw new Error('Variation data unavailable');
      return response.json();
    }).catch(error => { requests.delete(url); throw error; }));
    return requests.get(url);
  };
  customElements.define('maison-variation-picker', class extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      try {
        this.labels = JSON.parse(this.querySelector('[data-bv-labels]').textContent);
        const bucket = String(hash(this.dataset.productId) % 32).padStart(2, '0');
        const index = await load(this.asset('index', bucket));
        const groupId = index[this.dataset.productId]?.g;
        if (!/^[a-f0-9]{16}$/.test(groupId || '')) return;
        const groupBucket = String(parseInt(groupId.slice(0, 2), 16) % 32).padStart(2, '0');
        const groups = await load(this.asset('groups', groupBucket));
        const group = groups[groupId];
        if (!group || !this.isConnected) return;
        this.groupId = groupId;
        this.current = group.v.find(item => item.i === this.dataset.productId);
        if (!this.current) return;
        const options = new Map();
        for (const item of group.v) {
          if (!/^[^/?#\s]+$/u.test(item.h)) continue;
          const key = signature(item);
          if (!options.has(key) || item.i === this.current.i) options.set(key, item);
        }
        this.items = [...options.values()];
        if (this.items.length < 2) return;
        this.render();
        this.hidden = false;
        this.restorePosition();
      } catch (_) {
        this.hidden = true;
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.cropObserver?.disconnect();
      this.dialog?.remove();
    }
    asset(kind, bucket) {
      const prefix = kind === 'index' ? 'ma-variation-index' : 'maison-variation-groups';
      return `${this.dataset.base}${prefix}-${bucket}.json?v=${this.dataset.revision}`;
    }
    label(value) {
      return value === 'As pictured' ? this.labels.photos : value;
    }
    url(item) {
      return `${this.dataset.root}products/${encodeURIComponent(item.h)}`;
    }
    photo(item, className, size = 160) {
      if (!item.img) return element('span', 'bv-photo-fallback', this.labels.photos);
      const img = element('img', className);
      const url = new URL(item.img);
      url.searchParams.set('width', String(size));
      img.crossOrigin = 'anonymous';
      img.addEventListener('load', () => this.fitPhoto(img));
      img.src = url.href;
      img.alt = '';
      img.width = size;
      img.height = size;
      img.loading = 'lazy';
      return img;
    }
    fitPhoto(img) {
      const frame = img.parentElement;
      if (!frame || !img.isConnected || !img.naturalWidth) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(img, 0, 0, 64, 64);
        const pixels = context.getImageData(0, 0, 64, 64).data;
        const white = (x, y, limit = 238) => {
          const i = (y * 64 + x) * 4;
          return pixels[i + 3] < 80 || (pixels[i] > limit && pixels[i + 1] > limit && pixels[i + 2] > limit);
        };
        let edgeWhite = 0;
        for (let i = 0; i < 64; i++) edgeWhite += Number(white(i, 0)) + Number(white(i, 63)) + Number(white(0, i)) + Number(white(63, i));
        if (edgeWhite / 256 < .94) return;
        let left = 64, right = 0, top = 64, bottom = 0;
        for (let y = 1; y < 63; y++) for (let x = 1; x < 63; x++) if (!white(x, y, 224)) {
          left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
        }
        if (right - left < 4 || bottom - top < 4 || ((right - left) > 55 && (bottom - top) > 55)) return;
        const fit = () => {
          if (!img.isConnected || !frame.clientWidth || !frame.clientHeight) return;
          const scale = Math.min(frame.clientWidth / ((right - left + 5) / 64 * img.naturalWidth), frame.clientHeight / ((bottom - top + 5) / 64 * img.naturalHeight)) * .9;
          const width = img.naturalWidth * scale, height = img.naturalHeight * scale;
          Object.assign(img.style, { position: 'absolute', width: `${width}px`, height: `${height}px`, maxWidth: 'none', maxHeight: 'none', left: `${frame.clientWidth / 2 - (left + right) / 128 * width}px`, top: `${frame.clientHeight / 2 - (top + bottom) / 128 * height}px`, transform: 'none' });
        };
        fit();
        if ('ResizeObserver' in window) {
          this.photoFits ||= new WeakMap();
          this.photoFits.set(frame, fit);
          this.cropObserver ||= new ResizeObserver(entries => entries.forEach(entry => this.photoFits.get(entry.target)?.()));
          this.cropObserver.observe(frame);
        }
      } catch (_) {}
    }
    link(item, className) {
      const link = element('a', className);
      link.href = this.url(item);
      link.setAttribute('aria-label', `${this.labels.view} ${item.c}, ${item.m}, ${finish(item)}`);
      link.title = item.t;
      if (item.i === this.current.i) link.setAttribute('aria-current', 'true');
      link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (item.i === this.current.i) {
          event.preventDefault();
          this.dialog?.close();
          return;
        }
        try {
          sessionStorage.setItem('maison_variation_position', JSON.stringify({ group: this.groupId, handle: item.h, top: this.getBoundingClientRect().top, at: Date.now() }));
        } catch (_) {}
      });
      return link;
    }
    best(field, value, pool = this.items) {
      const weight = item => (item.c === this.current.c ? 8 : 0) + (item.m === this.current.m ? 6 : 0) + (finish(item) === finish(this.current) ? 3 : 0) + (item.i === this.current.i ? 1 : 0);
      return pool.filter(item => (field === 'finish' ? finish(item) : item[field]) === value).sort((a, b) => weight(b) - weight(a))[0];
    }
    row(key, field, pool, photo = false) {
      const value = field === 'finish' ? finish(this.current) : this.current[field];
      const values = sorted(pool.map(item => field === 'finish' ? finish(item) : item[field]));
      if (values.length < 2) return;
      const row = element('div', 'bv-row');
      const title = element('p', 'bv-row__title');
      title.append(element('span', '', this.labels[key]), element('strong', '', this.label(value)));
      const options = element('div', photo ? 'bv-options bv-options--photos' : 'bv-options');
      options.setAttribute('role', 'group');
      options.setAttribute('aria-label', this.labels[key]);
      for (const option of values) {
        const target = this.best(field, option, pool);
        const choice = this.link(target, photo ? 'bv-choice bv-choice--photo' : 'bv-choice bv-choice--pill');
        if (option === value) choice.classList.add('is-selected');
        if (photo) {
          const frame = element('span', 'bv-choice__frame');
          frame.append(this.photo(target));
          choice.append(frame, element('span', 'bv-choice__name', this.label(option)));
        } else choice.append(element('span', '', this.label(option)));
        options.append(choice);
      }
      row.append(title, options);
      this.append(row);
      requestAnimationFrame(() => {
        const selected = options.querySelector('.is-selected');
        if (selected && selected.offsetLeft > options.clientWidth * .7) options.scrollLeft = selected.offsetLeft - options.offsetLeft - options.clientWidth / 2 + selected.clientWidth / 2;
      });
    }
    render() {
      const heading = element('div', 'bv-flat-heading');
      heading.append(element('span', '', this.labels.colour));
      const actions = element('div', 'bv-flat-actions');
      const materials = sorted(this.items.map(item => item.m));
      if (materials.length > 1) {
        const control = element('details', 'bv-material-control');
        const summary = element('summary', '', this.labels.material);
        const panel = element('div', 'bv-material-panel');
        const label = element('label', '', this.labels.material);
        const select = element('select');
        select.setAttribute('aria-label', this.labels.material);
        materials.forEach(material => {
          const option = element('option', '', this.label(material));
          option.value = material; option.selected = material === this.current.m;
          select.append(option);
        });
        select.addEventListener('change', () => {
          const target = this.best('m', select.value);
          if (target) this.link(target, '').click();
        });
        label.append(select); panel.append(label); control.append(summary, panel);
        actions.append(control);
        document.addEventListener('click', event => {
          if (!control.contains(event.target)) control.open = false;
        }, { signal: this.abort.signal });
      }
      const all = element('button', 'bv-flat-all', this.labels.explore);
      all.type = 'button'; all.setAttribute('aria-haspopup', 'dialog');
      all.addEventListener('click', () => this.openDialog());
      heading.append(all, actions);
      const groups = new Map();
      for (const item of this.items) {
        const key = item.c + '|' + item.m;
        const pool = this.items.filter(other => other.c === item.c && other.m === item.m);
        groups.set(key, pool.find(other => other.i === this.current.i) || this.best('c', item.c, pool));
      }
      let choices = this.items.length <= 8 ? [...this.items] : [...groups.values()];
      choices.sort((a,b) => (b.i === this.current.i) - (a.i === this.current.i) || a.c.localeCompare(b.c) || a.m.localeCompare(b.m));
      const colours = new Set(), first = [], rest = [];
      for (const item of choices) {
        if (!colours.has(item.c)) { first.push(item); colours.add(item.c); }
        else rest.push(item);
      }
      choices = first.concat(rest);
      const strip = element('div', 'bv-flat-options');
      strip.setAttribute('role', 'group'); strip.setAttribute('aria-label', 'Available colours and materials');
      for (const item of choices) {
        const choice = this.link(item, 'bv-flat-choice');
        if (item.i === this.current.i) choice.classList.add('is-selected');
        const frame = element('span', 'bv-choice__frame');
        frame.append(this.photo(item, '', 140));
        const name = item.c === 'As pictured' ? this.label(item.m) : this.label(item.c);
        choice.append(frame, element('span', 'bv-flat-name', name));
        strip.append(choice);
      }
      this.replaceChildren(heading, strip);
    }
    openDialog() {
      if (!this.dialog) this.buildDialog();
      this.dialog.showModal();
    }
    buildDialog() {
      const dialog = element('dialog', 'bv-dialog');
      const headingId = `MaisonVariationHeading-${this.dataset.productId}`;
      dialog.setAttribute('aria-labelledby', headingId);
      const top = element('div', 'bv-dialog__top');
      const copy = element('div');
      copy.append(element('p', 'bv-dialog__eyebrow', this.labels.heading));
      const title = element('h2', '', this.labels.modal_heading);
      title.id = headingId;
      copy.append(title);
      const close = element('button', 'bv-dialog__close', '×');
      close.type = 'button';
      close.setAttribute('aria-label', this.labels.close);
      close.addEventListener('click', () => dialog.close());
      top.append(copy, close);
      const filters = element('div', 'bv-filters');
      const search = element('input', 'bv-search');
      search.type = 'search';
      search.placeholder = this.labels.search;
      search.setAttribute('aria-label', this.labels.search);
      const select = (field, all, label) => {
        const node = element('select');
        node.setAttribute('aria-label', label);
        const first = element('option', '', all);
        first.value = '';
        node.append(first);
        sorted(this.items.map(item => item[field])).forEach(value => {
          const option = element('option', '', this.label(value));
          option.value = value;
          node.append(option);
        });
        return node;
      };
      const colour = select('c', this.labels.all_colours, this.labels.colour);
      const material = select('m', this.labels.all_materials, this.labels.material);
      filters.append(search, colour, material);
      const status = element('p', 'bv-dialog__status');
      status.setAttribute('aria-live', 'polite');
      const grid = element('div', 'bv-dialog__grid');
      const update = () => {
        const term = search.value.toLocaleLowerCase().trim();
        const visible = this.items.filter(item => (!colour.value || item.c === colour.value) && (!material.value || item.m === material.value) && `${item.c} ${item.m} ${finish(item)} ${item.t}`.toLocaleLowerCase().includes(term));
        status.textContent = visible.length ? `${visible.length} ${visible.length === 1 ? this.labels.shown_one : this.labels.shown}` : this.labels.no_results;
        grid.querySelectorAll('.bv-card__photo').forEach(frame => this.cropObserver?.unobserve(frame));
        grid.replaceChildren();
        visible.sort((a, b) => (b.i === this.current.i) - (a.i === this.current.i) || a.c.localeCompare(b.c)).forEach(item => {
          const card = this.link(item, 'bv-card');
          const photo = element('div', 'bv-card__photo');
          photo.append(this.photo(item, '', 360));
          if (item.i === this.current.i) photo.append(element('span', 'bv-card__badge', this.labels.current));
          const body = element('div', 'bv-card__body');
          body.append(element('strong', '', this.label(item.c)), element('span', '', this.label(item.m)), element('small', '', this.label(finish(item))));
          card.append(photo, body);
          grid.append(card);
        });
      };
      filters.addEventListener('input', update);
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      dialog.append(top, filters, status, grid);
      document.body.append(dialog);
      this.dialog = dialog;
      update();
    }
    restorePosition() {
      try {
        const saved = JSON.parse(sessionStorage.getItem('maison_variation_position') || 'null');
        if (saved?.handle === this.dataset.handle) {
          sessionStorage.removeItem('maison_variation_position');
          if (saved.group === this.groupId && Date.now() - saved.at < 30000 && Number.isFinite(saved.top)) requestAnimationFrame(() => window.scrollBy({ top: this.getBoundingClientRect().top - Math.max(90, saved.top), behavior: 'instant' }));
        }
      } catch (_) {}
    }
  });
})();
